/**
 * King Arthur — „Battle of Legends, Volume One".
 *
 * Keď útočí King Arthur, smie útok boostovať (`attackImmediate`). V papierovej
 * hre sa boost kladie lícom dole spolu s útokom a pri zrušení útočnej karty
 * prepadne bez efektu; tu sa voľba ponúkne v kroku IMMEDIATELY útočníka —
 * teda až keď je jasné, či obranca kartu zrušil. Pri zrušenej karte sa
 * nepýta vôbec (karta sa neminie). Obranca ju vidí rovnako ako pri odhalení.
 */

import { neighbors, shareZone } from '../map.js';
import type { Ctx } from '../ops.js';
import {
  askChoice,
  dealDamage,
  drawCard,
  drawSpecific,
  emit,
  getMap,
  getPlayer,
  returnFromDiscard,
  searchDeck,
  setHealth,
  shuffleDeck,
} from '../ops.js';
import { areEnemies } from '../players.js';
import type { PlayerIndex, SpaceId } from '../types.js';
import { draw, feint, momentousShift, regroup, skirmish } from './common.js';
import { askBoost, askMove, boostSources, fighterOrNull } from './helpers.js';
import { bewilderment } from './sunWukong.js';
import type { CardEffect, EffectRegistry, HeroSpecial } from './types.js';

const ARTHUR = 'king-arthur';
const MERLIN = 'merlin';
const EXCALIBUR = 'king-arthur.excalibur';
const PROPHECY = 'king-arthur.prophecy';
const SPIRITS = 'king-arthur.restless-spirits';

const figure = (ctx: Ctx, player: PlayerIndex, defId: string) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === defId) ?? null;

const spaceName = (ctx: Ctx, id: SpaceId) => getMap(ctx).spaces.find((s) => s.id === id)?.label ?? id;

/** „DURING: You may BOOST this attack. (This is in addition to any boost from King Arthur's special ability.)" */
const nobleSacrifice: CardEffect = {
  duringChoice: (e) => askBoost(e, 'Noble Sacrifice: smieš boostovať útok'),
};

/** „Move King Arthur up to 5 spaces." */
const divineIntervention: CardEffect = {
  after: (e) => {
    const arthur = figure(e.ctx, e.player, ARTHUR);
    if (arthur) askMove(e, { fighters: [arthur.id], max: 5, label: 'Divine Intervention: presuň Kráľa Artuša až o 5' });
  },
};

/** „If King Arthur has 4 or less health but is not defeated, set his health to 8." */
const holyGrail: CardEffect = {
  after: (e) => {
    const arthur = figure(e.ctx, e.player, ARTHUR);
    if (arthur && arthur.hp <= 4) setHealth(e.ctx, arthur.id, 8);
  },
};

/**
 * „Search your deck and discard pile for the EXCALIBUR card. Add it to your
 * hand. If you searched your deck, shuffle it." Excalibur je v balíčku jeden —
 * hľadanie ho nájde samo, niet z čoho vyberať.
 */
const ladyOfTheLake: CardEffect = {
  scheme: (e) => {
    const seat = getPlayer(e.ctx.state, e.player);
    const isExcalibur = (id: string) => e.ctx.state.cards[id]?.defId === EXCALIBUR;
    const inDeck = seat.deck.find(isExcalibur);
    if (inDeck) {
      searchDeck(e.ctx, e.player, inDeck);
      shuffleDeck(e.ctx, e.player);
      return;
    }
    const inDiscard = seat.discard.find(isExcalibur);
    if (inDiscard) returnFromDiscard(e.ctx, e.player, [inDiscard]);
  },
};

/**
 * „Look at the top 4 cards of your deck. Add 2 of them to your hand and put
 * the other 2 back on top of your deck, in any order." Karty vidí len on
 * (voľba `from: 'deck'`); `left` = koľko ešte berie (2, 1), potom poradie.
 */
function askProphecy(ctx: Ctx, player: PlayerIndex, source: string, cards: string[], left: number): void {
  askChoice(ctx, {
    kind: 'card',
    player,
    source,
    label:
      left > 0
        ? `Prophecy: vezmi si kartu do ruky (${3 - left} z 2)`
        : 'Prophecy: ktorá z ostatných pôjde navrch balíčka?',
    optional: left === 0,
    from: 'deck',
    cards,
    purpose: 'effect',
    left,
  });
}

const prophecy: CardEffect = {
  scheme: (e) => {
    const top = getPlayer(e.ctx.state, e.player).deck.slice(0, 4);
    if (top.length === 0) return;
    askProphecy(e.ctx, e.player, e.cardId, top, Math.min(2, top.length));
  },
  onCardChosen: (ctx, choice, cardId) => {
    const rest = (choice.cards ?? []).filter((id) => id !== cardId);
    const left = choice.left ?? 0;
    if (left > 0) {
      drawSpecific(ctx, choice.player, cardId);
      if (left > 1 && rest.length > 0) askProphecy(ctx, choice.player, choice.source ?? PROPHECY, rest, left - 1);
      else if (rest.length === 2) askProphecy(ctx, choice.player, choice.source ?? PROPHECY, rest, 0);
      return;
    }
    // Vybraná ide navrch: zvyšok sa presunie pod ňu — z ruky nie, sú v balíčku.
    const deck = getPlayer(ctx.state, choice.player).deck;
    deck.splice(deck.indexOf(cardId), 1);
    deck.unshift(cardId);
    emit(ctx, { type: 'deckReordered', player: choice.player, owner: choice.player });
  },
};

/** „If you won the combat, draw 2 cards." */
const aidTheChosenOne: CardEffect = {
  after: (e) => {
    if (!e.won) return;
    drawCard(e.ctx, e.player);
    drawCard(e.ctx, e.player);
  },
};

/**
 * „Choose any space in Merlin's zone. Deal 2 damage to each opposing fighter
 * in that space and in one adjacent space. If at least one fighter is
 * defeated this way, draw 1 card." Priestory sa volia `option` (sú verejné).
 */
const restlessSpirits: CardEffect = {
  scheme: (e) => {
    const merlin = figure(e.ctx, e.player, MERLIN);
    if (!merlin) return;
    const map = getMap(e.ctx);
    const spaces = map.spaces.map((s) => s.id).filter((id) => id === merlin.spaceId || shareZone(map, id, merlin.spaceId));
    askChoice(e.ctx, {
      kind: 'option',
      player: e.player,
      source: e.cardId,
      label: 'Restless Spirits: vyber priestor v Merlinovej zóne',
      optional: false,
      options: spaces.map((id) => ({ label: spaceName(e.ctx, id), follow: { kind: 'effect' as const, effect: SPIRITS, data: `first:${id}` } })),
    });
  },
  onFollow: (ctx, choice, data) => {
    const [step, first, second] = data.split(':') as [string, SpaceId, SpaceId | undefined];
    if (step === 'first') {
      const around = neighbors(getMap(ctx), first);
      askChoice(ctx, {
        kind: 'option',
        player: choice.player,
        source: choice.source,
        label: 'Restless Spirits: a jeden susedný priestor',
        optional: false,
        options: around.map((id) => ({
          label: spaceName(ctx, id),
          follow: { kind: 'effect' as const, effect: SPIRITS, data: `hit:${first}:${id}` },
        })),
      });
      return;
    }
    const targets = ctx.state.fighters
      .filter((f) => (f.spaceId === first || f.spaceId === second) && areEnemies(ctx.state, f.owner, choice.player))
      .map((f) => f.id);
    let defeated = false;
    for (const id of targets) {
      dealDamage(ctx, id, 2);
      if (!fighterOrNull(ctx, id)) defeated = true;
    }
    if (defeated) drawCard(ctx, choice.player);
  },
};

/** „Move each fighter up to 3 spaces. (This includes opposing fighters.)" */
const commandTheStorms: CardEffect = {
  scheme: (e) => {
    for (const fighter of e.ctx.state.fighters) {
      askMove(e, { fighters: [fighter.id], max: 3, label: `Command the Storms: presuň ${fighter.name} až o 3` });
    }
  },
};

/** „Move your fighter up to 4 spaces." */
export const swiftStrike: CardEffect = {
  after: (e) => {
    if (e.fighterId) askMove(e, { fighters: [e.fighterId], max: 4, label: 'Swift Strike: presuň svoju figúrku až o 4' });
  },
};

export const kingArthurCards: EffectRegistry = {
  'king-arthur.skirmish': skirmish(2),
  'king-arthur.noble-sacrifice': nobleSacrifice,
  'king-arthur.the-aid-of-morgana': draw(2),
  'king-arthur.divine-intervention': divineIntervention,
  'king-arthur.the-holy-grail': holyGrail,
  'king-arthur.the-lady-of-the-lake': ladyOfTheLake,
  [PROPHECY]: prophecy,
  'king-arthur.bewilderment': bewilderment,
  'king-arthur.aid-the-chosen-one': aidTheChosenOne,
  [SPIRITS]: restlessSpirits,
  'king-arthur.command-the-storms': commandTheStorms,
  'king-arthur.swift-strike': swiftStrike,
  'king-arthur.momentous-shift': momentousShift,
  'king-arthur.feint': feint,
  'king-arthur.regroup': regroup,
  // Excalibur: bez efektu.
};

export const kingArthurSpecial: HeroSpecial = {
  name: 'Excalibur',
  /** „When King Arthur attacks, you may BOOST that attack." */
  attackImmediate: (ctx, player, fighterId) => {
    const combat = ctx.state.combat;
    if (fighterOrNull(ctx, fighterId)?.defId !== ARTHUR || !combat) return;
    // Zrušená útočná karta: boost by prepadol bez efektu — nepýtame sa.
    if (combat.cancelled?.attacker) return;
    if (boostSources(ctx, player).length === 0) return;
    askChoice(ctx, {
      kind: 'card',
      player,
      source: null,
      label: 'Kráľ Artuš: smieš boostovať útok',
      optional: true,
      from: 'hand',
      purpose: 'boost',
      side: 'attacker',
    });
  },
};

